import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import '../../../../dist/assets/stylesheets/css/trialLiveLayout.css';
import { trackForCamera } from '../../../util/cameraReprojection';
import {
  cameraGridSlots,
  formatPlaybackReadout,
  frameFromMediaTime,
  mediaTimeForFrame,
  playbackFps,
  framesBehindWallClock,
  playbackFrameNumber,
  slaveTrimRate,
  videoFrameCount,
} from '../../../util/trialLiveLayout';
import { trialScrubFrame, trialScrubStep } from '../../../util/trialPlaybackKeys';
import CameraBoneOverlay from './CameraBoneOverlay';
import SkeletonStage from './SkeletonStage';

function playVideo(el) {
  if (!el || typeof el.play !== 'function') return;
  const pending = el.play();
  if (pending && typeof pending.catch === 'function') {
    pending.catch((err) => {
      if (!err || err.name !== 'AbortError') {
        console.error('Video play failed:', err);
      }
    });
  }
}

const CHART_SYNC_MS = 50;
const WALL_CLOCK_SLACK_FRAMES = 2;
const WALL_CLOCK_RESET_FRAMES = 30;

function watchPresentedFrames(video, onFrame) {
  let stopped = false;
  let handle = 0;
  let lastMedia = -1;
  const supports = typeof video.requestVideoFrameCallback === 'function';
  const onVideoFrame = (_now, metadata) => {
    if (stopped) return;
    let mediaTime = metadata && Number.isFinite(metadata.mediaTime) ? metadata.mediaTime : null;
    // Steady playback trusts the frame callback. currentTime is read only when
    // that timestamp is missing or jumps, because the getter stalls the decoder.
    const steady = mediaTime != null && mediaTime > 0 && Math.abs(mediaTime - lastMedia) < 0.25;
    if (!steady) {
      const current = video.currentTime;
      if (mediaTime == null) {
        mediaTime = Number.isFinite(current) ? current : 0;
      } else if (Number.isFinite(current) && mediaTime + 0.25 < current) {
        mediaTime = current;
      }
    }
    lastMedia = mediaTime;
    onFrame(mediaTime);
    if (!stopped) handle = video.requestVideoFrameCallback(onVideoFrame);
  };
  if (supports) {
    handle = video.requestVideoFrameCallback(onVideoFrame);
  }
  return () => {
    stopped = true;
    if (supports && typeof video.cancelVideoFrameCallback === 'function') {
      video.cancelVideoFrameCallback(handle);
    }
  };
}

function LiveCamera({
  index,
  camera,
  track,
  joints,
  edges,
  assignVideo,
  onLoadedMetadata,
  onError,
}) {
  const videoRef = useRef(null);
  const setVideo = useCallback((el) => {
    videoRef.current = el;
    assignVideo(el);
  }, [assignVideo]);
  return (
    <>
      <video
        ref={setVideo}
        className={index === 0 ? 'view' : undefined}
        src={camera.src}
        playsInline
        muted
        preload="auto"
        onLoadedMetadata={onLoadedMetadata}
        onError={onError}
      />
      <CameraBoneOverlay videoRef={videoRef} track={track} joints={joints} edges={edges} />
      <span className="trial-live-cam-label">{camera.label}</span>
    </>
  );
}

function TrialLiveLayout({
  layout,
  error = false,
  videoRef,
  onLoadedMetadata,
  onError,
  onTimeSync,
  onSeek,
  onLayoutModeChange,
}) {
  const masterRef = useRef(null);
  const slaveRefs = useRef([]);
  const stageRef = useRef(null);
  const timelineRef = useRef(null);
  const readoutRef = useRef(null);
  const isScrubbingRef = useRef(false);
  const scrubTargetRef = useRef(null);
  const resumeAfterScrubRef = useRef(false);
  const scrubRetryRef = useRef([]);
  const pendingAlignRef = useRef(false);
  const relockRef = useRef(false);
  const scrubToRef = useRef(null);
  const playAnchorRef = useRef(null);
  const playTokenRef = useRef(0);
  const lastChartSyncRef = useRef(0);
  const lastFrameRef = useRef(-1);
  const lastTimelineRef = useRef('');
  const onTimeSyncRef = useRef(onTimeSync);
  const onSeekRef = useRef(onSeek);
  const [isPlaying, setIsPlaying] = useState(false);
  const [layoutMode, setLayoutMode] = useState('compact');
  onTimeSyncRef.current = onTimeSync;
  onSeekRef.current = onSeek;

  const meta = layout?.meta;
  const pose = layout?.pose;
  const reprojection = layout?.reprojection;
  const slots = cameraGridSlots(meta?.cameras);

  const videoCallbacks = useRef([]);
  const assignVideo = (index) => {
    if (!videoCallbacks.current[index]) {
      videoCallbacks.current[index] = (el) => {
        if (index === 0) {
          masterRef.current = el;
          if (videoRef) videoRef.current = el;
        } else {
          slaveRefs.current[index - 1] = el;
        }
      };
    }
    return videoCallbacks.current[index];
  };

  const videoNodes = useCallback(
    () => [masterRef.current, ...slaveRefs.current].filter(Boolean),
    []
  );

  const assignClock = useCallback((el, time) => {
    if (!el || !Number.isFinite(time) || !Number.isFinite(el.currentTime)) return;
    if (Math.abs(el.currentTime - time) <= 1e-3) return;
    try {
      el.currentTime = time;
    } catch (err) {
      if (err && err.name !== 'InvalidStateError' && err.name !== 'NotSupportedError') {
        console.error('Slave seek failed:', err);
      }
    }
  }, []);

  const syncSlaves = useCallback((clock, force) => {
    if (!meta || !Number.isFinite(clock) || !force) return;
    slaveRefs.current.forEach((el) => {
      if (!el || !Number.isFinite(el.currentTime)) return;
      if (Math.abs(el.currentTime - clock) <= 1e-3) return;
      if (el.playbackRate !== 1) el.playbackRate = 1;
      assignClock(el, clock);
    });
  }, [assignClock, meta]);

  const trimSlaves = useCallback(() => {
    const master = masterRef.current;
    if (!master || master.paused || !meta) return;
    const fps = playbackFps(meta, reprojection);
    const cameras = [master, ...slaveRefs.current.filter(Boolean)]
      .filter((el) => !el.seeking)
      .map((el) => ({ el, shown: Number(el.dataset.presentedFrame) }))
      .filter((cam) => Number.isFinite(cam.shown));
    if (cameras.length < 2) return;
    // Steer every camera, master included, toward the median frame. A busy
    // machine cannot run the lagging cameras faster, so the leader slows.
    const sorted = cameras.map((cam) => cam.shown).sort((a, b) => a - b);
    const reference = sorted[Math.floor((sorted.length - 1) / 2)];
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    let behind = framesBehindWallClock(playAnchorRef.current, now, fps, reference);
    // A buffering stall is accepted rather than chased; start real time over.
    if (behind > WALL_CLOCK_RESET_FRAMES) {
      playAnchorRef.current = { wallMs: now, frame: reference };
      behind = 0;
    }
    // Behind real time, nobody is slowed down: the leaders keep running at
    // 1x and the laggers speed up, so the group does not settle below 1x.
    const floor = behind > WALL_CLOCK_SLACK_FRAMES ? 1 : 0;
    cameras.forEach(({ el, shown }) => {
      const rate = slaveTrimRate(reference - shown);
      if (rate == null && el !== master) {
        el.playbackRate = 1;
        assignClock(el, mediaTimeForFrame(reference, fps));
        return;
      }
      const next = Math.max(floor, rate == null ? 1 : rate);
      if (el.playbackRate !== next) el.playbackRate = next;
    });
  }, [assignClock, meta, reprojection]);

  const alignPresented = useCallback(() => {
    const master = masterRef.current;
    if (!master || !meta) return false;
    const fps = playbackFps(meta, reprojection);
    const masterFrame = Number(master.dataset.presentedFrame);
    if (!Number.isFinite(masterFrame)) return true;
    let waiting = false;
    slaveRefs.current.forEach((el) => {
      if (!el) return;
      if (el.seeking) {
        waiting = true;
        return;
      }
      const presented = Number(el.dataset.presentedFrame);
      if (!Number.isFinite(presented)) {
        waiting = true;
        return;
      }
      if (presented === masterFrame) return;
      // Land inside the master's camera frame. If the clock is already
      // there but the decoder is showing the neighbor, nudge within the frame.
      const target = mediaTimeForFrame(masterFrame, fps);
      const nudged = Math.abs(el.currentTime - target) <= 1e-3
        ? (masterFrame + 0.4) / fps
        : target;
      assignClock(el, nudged);
      waiting = true;
    });
    return waiting;
  }, [assignClock, meta, reprojection]);

  const applyPresented = useCallback((mediaTime, scrub) => {
    if (!meta || !pose || !Number.isFinite(mediaTime)) return;
    const frame = frameFromMediaTime(mediaTime, meta.fps, meta.frameCount);
    const rate = playbackFps(meta, reprojection);
    const videoFrame = frameFromMediaTime(mediaTime, rate, videoFrameCount(meta, reprojection, rate));
    if (frame !== lastFrameRef.current) {
      lastFrameRef.current = frame;
      stageRef.current?.drawFrame(frame);
    }
    if (readoutRef.current) {
      const label = formatPlaybackReadout(videoFrame, mediaTime);
      if (readoutRef.current.textContent !== label) readoutRef.current.textContent = label;
    }
    const input = timelineRef.current;
    if (input && !isScrubbingRef.current) {
      const next = String(videoFrame);
      if (lastTimelineRef.current !== next) {
        lastTimelineRef.current = next;
        if (input.value !== next) input.value = next;
      }
    }
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    if (scrub || now - lastChartSyncRef.current >= CHART_SYNC_MS) {
      lastChartSyncRef.current = now;
      onTimeSyncRef.current?.(mediaTime);
    }
    if (scrub) syncSlaves(mediaTime, true);
  }, [meta, pose, reprojection, syncSlaves]);

  useEffect(() => {
    const video = masterRef.current;
    if (!video || !meta) return undefined;
    const onSeeked = () => {
      const target = scrubTargetRef.current;
      // A drag queues several seeks. An earlier one finishing must not
      // pull the other cameras back to the time it landed on.
      if (Number.isFinite(target) && Math.abs(video.currentTime - target) > 1e-3) {
        const tries = scrubRetryRef.current[0] || 0;
        if (tries < 2) {
          scrubRetryRef.current[0] = tries + 1;
          assignClock(video, target);
          syncSlaves(target, true);
        }
        return;
      }
      if (Number.isFinite(target)) scrubTargetRef.current = null;
      if (isScrubbingRef.current) return;
      applyPresented(video.currentTime, true);
    };
    const onSlaveSeeked = (event) => {
      const target = scrubTargetRef.current;
      const el = event.currentTarget;
      if (!Number.isFinite(target) || !el) return;
      if (Math.abs(el.currentTime - target) <= 1e-3) return;
      const index = slaveRefs.current.indexOf(el) + 1;
      const tries = scrubRetryRef.current[index] || 0;
      if (tries >= 2) return;
      scrubRetryRef.current[index] = tries + 1;
      assignClock(el, target);
    };
    const onPlay = () => {
      if (!isScrubbingRef.current && !relockRef.current) setIsPlaying(true);
      if (isScrubbingRef.current) return;
      slaveRefs.current.forEach((el) => {
        if (el && el.paused) playVideo(el);
      });
    };
    const onPause = () => {
      if (!isScrubbingRef.current && !relockRef.current) setIsPlaying(false);
      if (isScrubbingRef.current) return;
      slaveRefs.current.forEach((el) => {
        if (el && !el.paused) el.pause();
      });
    };
    const onSeeking = () => {
      if (!Number.isFinite(video.currentTime)) return;
      const target = scrubTargetRef.current;
      if (Number.isFinite(target)) {
        syncSlaves(target, true);
        return;
      }
      syncSlaves(video.currentTime, false);
    };
    const supportsFrameClock = typeof video.requestVideoFrameCallback === 'function';
    const onTime = () => {
      if (supportsFrameClock || isScrubbingRef.current || video.paused) return;
      if (!Number.isFinite(video.currentTime)) return;
      syncSlaves(video.currentTime, false);
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    const slaves = slaveRefs.current.filter(Boolean);
    slaves.forEach((el) => el.addEventListener('seeked', onSlaveSeeked));
    setIsPlaying(!video.paused);
    applyPresented(video.currentTime, false);
    const stop = watchPresentedFrames(video, (mediaTime) => {
      if (isScrubbingRef.current || Number.isFinite(scrubTargetRef.current)) return;
      applyPresented(mediaTime, false);
      if (video.paused || video.seeking) return;
      trimSlaves();
    });
    const onPresented = () => {
      if (isScrubbingRef.current || Number.isFinite(scrubTargetRef.current)) return;
      const master = masterRef.current;
      if (!master) return;
      if (!master.paused) {
        trimSlaves();
        return;
      }
      if (!pendingAlignRef.current) return;
      const waiting = alignPresented();
      if (!waiting) pendingAlignRef.current = false;
    };
    const nodes = [video, ...slaves];
    nodes.forEach((el) => el.addEventListener('presentedframe', onPresented));
    return () => {
      stop();
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      slaves.forEach((el) => el.removeEventListener('seeked', onSlaveSeeked));
      nodes.forEach((el) => el.removeEventListener('presentedframe', onPresented));
    };
  }, [alignPresented, assignClock, meta, applyPresented, syncSlaves, trimSlaves]);

  useEffect(() => {
    onLayoutModeChange?.(layoutMode);
  }, [layoutMode, onLayoutModeChange]);

  const playSyncTimersRef = useRef([]);
  const clearPlaySync = useCallback(() => {
    playSyncTimersRef.current.forEach((id) => window.clearTimeout(id));
    playSyncTimersRef.current = [];
  }, []);
  const playTogether = useCallback(() => {
    const master = masterRef.current;
    if (!master) return;
    const clock = Number.isFinite(master.currentTime) ? master.currentTime : 0;
    const fps = playbackFps(meta, reprojection);
    const frame = playbackFrameNumber(clock, fps);
    const target = mediaTimeForFrame(frame, fps);
    const frameSpan = 1.5 / fps;
    const nodes = [master, ...slaveRefs.current.filter(Boolean)];
    nodes.forEach((el) => {
      el.playbackRate = 1;
      // Every camera stays muted. Chrome stalls a second video that still has audio.
      el.muted = true;
      if (el === master || !Number.isFinite(el.currentTime)) return;
      if (Math.abs(el.currentTime - target) > frameSpan) assignClock(el, target);
    });
    playAnchorRef.current = {
      wallMs: typeof performance !== 'undefined' ? performance.now() : 0,
      frame,
    };
    nodes.forEach(playVideo);
  }, [assignClock, meta, reprojection]);

  useEffect(() => clearPlaySync, [clearPlaySync]);

  const togglePlay = useCallback(() => {
    const master = masterRef.current;
    if (!master) return;
    const nodes = [master, ...slaveRefs.current.filter(Boolean)];
    if (master.paused && !relockRef.current) playTogether();
    else {
      playTokenRef.current += 1;
      relockRef.current = false;
      clearPlaySync();
      nodes.forEach((el) => {
        el.playbackRate = 1;
        el.pause();
      });
      // Park every camera, master included, inside the frame the master is
      // showing. The master can present one more frame after pause(), so
      // checking the others against it right now is not enough.
      const fps = playbackFps(meta, reprojection);
      const shown = Number(master.dataset.presentedFrame);
      const frame = Number.isFinite(shown) ? shown : playbackFrameNumber(master.currentTime, fps);
      scrubToRef.current?.(frame);
    }
  }, [clearPlaySync, meta, playTogether, reprojection]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.code !== 'Space' && event.key !== ' ') return;
      const tag = event.target?.tagName;
      const type = event.target?.getAttribute?.('type');
      if (tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (tag === 'INPUT' && type !== 'range') return;
      event.preventDefault();
      togglePlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay]);

  useEffect(() => {
    const onKey = (event) => {
      const master = masterRef.current;
      if (!master || !meta) return;
      const step = trialScrubStep(event);
      if (step == null) return;
      event.preventDefault();
      event.stopPropagation();
      const fps = playbackFps(meta, reprojection);
      const count = videoFrameCount(meta, reprojection, fps);
      const frame = playbackFrameNumber(master.currentTime, fps);
      scrubToRef.current?.(trialScrubFrame(frame, count, step));
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [meta, reprojection]);

  const scrubTo = (frameIndex) => {
    if (!Number.isFinite(frameIndex) || !meta) return;
    const rate = playbackFps(meta, reprojection);
    const count = videoFrameCount(meta, reprojection, rate);
    const frame = Math.max(0, Math.min(count - 1, Math.round(frameIndex)));
    const snapped = mediaTimeForFrame(frame, rate);
    scrubTargetRef.current = snapped;
    scrubRetryRef.current = [];
    pendingAlignRef.current = true;
    assignClock(masterRef.current, snapped);
    applyPresented(snapped, true);
    onSeekRef.current?.(snapped);
  };
  scrubToRef.current = scrubTo;

  const beginScrub = () => {
    const master = masterRef.current;
    resumeAfterScrubRef.current = Boolean(master && !master.paused);
    isScrubbingRef.current = true;
    if (resumeAfterScrubRef.current) videoNodes().forEach((el) => el.pause());
  };

  const endScrub = () => {
    if (!isScrubbingRef.current) return;
    const time = Number(timelineRef.current && timelineRef.current.value);
    if (Number.isFinite(time)) scrubTo(time);
    const resume = resumeAfterScrubRef.current;
    resumeAfterScrubRef.current = false;
    isScrubbingRef.current = false;
    const stillSeeking = videoNodes().some((el) => el.seeking);
    if (!stillSeeking) scrubTargetRef.current = null;
    if (resume) playTogether();
  };

  const endScrubRef = useRef(endScrub);
  endScrubRef.current = endScrub;
  useEffect(() => {
    const onUp = () => endScrubRef.current();
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const frameCount = meta
    ? videoFrameCount(meta, reprojection, playbackFps(meta, reprojection))
    : 1;
  const rows = Math.max(3, Math.ceil(slots.length / 2));

  return (
    <div id="trial-live-layout" className={`trial-live-layout trial-live-layout--${layoutMode}`}>
      <div
        className="trial-live-stage trial-video-container"
        data-testid="trial-live-stage"
        style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
      >
        {meta && pose ? (
          <>
            {slots.map((slot, index) => (
              <div
                key={slot.key}
                className={
                  slot.camera
                    ? 'trial-live-cam'
                    : 'trial-live-cam trial-live-cam--empty'
                }
                style={{ gridColumn: slot.column, gridRow: slot.row }}
                data-testid={slot.camera ? `trial-live-cam-${index}` : undefined}
              >
                {slot.camera ? (
                  <LiveCamera
                    index={index}
                    camera={slot.camera}
                    track={trackForCamera(reprojection, slot.camera)}
                    joints={reprojection?.joints}
                    edges={reprojection?.edges}
                    assignVideo={assignVideo(index)}
                    onLoadedMetadata={index === 0 ? onLoadedMetadata : undefined}
                    onError={index === 0 ? onError : undefined}
                  />
                ) : null}
              </div>
            ))}
            {slots.length % 2 === 1 ? (
              <div className="trial-live-cam trial-live-cam--empty" style={{ gridColumn: 3, gridRow: rows }} data-testid="trial-live-empty" />
            ) : null}
            <div className="trial-live-center" style={{ gridRow: `1 / span ${rows}` }}>
              <SkeletonStage ref={stageRef} meta={meta} pose={pose} />
            </div>
          </>
        ) : (
          <p className="trial-live-status" role="status">
            {error ? 'Live view is not available for this trial.' : 'Loading live view…'}
          </p>
        )}
      </div>
      <div className="trial-controls trial-live-footer">
        <button
          type="button"
          className="trial-control-btn"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={togglePlay}
          disabled={!meta}
        >
          <Icon
            icon={isPlaying ? 'material-symbols:pause' : 'material-symbols:play-arrow'}
          />
        </button>
        <input
          ref={timelineRef}
          className="trial-timeline"
          type="range"
          aria-label="Playback position"
          min={0}
          max={Math.max(0, frameCount - 1)}
          step={1}
          defaultValue={0}
          disabled={!meta}
          onPointerDown={beginScrub}
          onPointerUp={endScrub}
          onPointerCancel={endScrub}
          onChange={(event) => scrubTo(Number(event.target.value))}
        />
        <span ref={readoutRef} className="trial-live-readout" data-testid="trial-live-readout">
          {formatPlaybackReadout(0, 0)}
        </span>
        <button
          type="button"
          className="trial-control-btn"
          aria-label={layoutMode === 'wide' ? 'Compact view' : 'Wide view'}
          aria-pressed={layoutMode === 'wide'}
          onClick={() => setLayoutMode((mode) => (mode === 'wide' ? 'compact' : 'wide'))}
        >
          <Icon icon="material-symbols:aspect-ratio" />
        </button>
      </div>
    </div>
  );
}

export default TrialLiveLayout;
